/* eslint-disable no-unused-expressions */
/* eslint-disable no-unused-vars */
import React from 'react';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { Grid, MenuItem, Box, FormControl, FormHelperText, FormGroup } from '@mui/material';
import { Formik } from 'formik';
import { useMutation } from '@apollo/client';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import TextField from '@atoms/TextField';
import Typography from '@atoms/Typography';
import Checkbox from '@atoms/Checkbox';
import Radio from '@atoms/Radio';
import FieldErrors from '@atoms/FieldErrors';
import LocationInput from '@atoms/LocationInput';
import Spacer from '@atoms/Spacer';
import MutationsStatus from '@molecules/MutationsStatus';
import PhoneInput from '@molecules/PhoneInput';
import { useTranslation } from '@app/config/i18n';
import {
  CREATE_APPLICANT_FEEDBACK_MUTATION,
  UPDATE_APPLICANT_FEEDBACK_MUTATION,
} from '@app/apollo/mutations';
import { FIELDS_ENUM, SUBTYPE_ENUM } from '@app/constants';
import GroupMembers from '@app/components/organisms/opportunities/OpportunityRegistrationViewer/GroupMembers';
import { redirectToRoute } from '@app/helpers';
import { ApplyButton, FormField, FormLabel } from './style';

const gridSizes = { xs: 12, sm: 12, md: 12, lg: 12 };

const FeedbackForm = ({
  feedbackApplicationData,
  feedbackForm,
  initialValue,
  setInitialValue,
  feedbackDataId,
  cancelRegistration,
  giveFeedback,
  refetch,
}) => {
  const { t } = useTranslation(['wizard', 'common', 'applications', 'footer', 'opportunity']);

  const newApplicationCompleted = () => {
    setTimeout(() => {
      cancelRegistration();
      refetch();
    }, 1000);
    if (giveFeedback) {
      redirectToRoute({}, 'view_opportunity', { params: { slug: initialValue?.opportunity } });
    }
  };

  const handleBack = () => {
    if (giveFeedback) {
      redirectToRoute({}, 'view_opportunity', {
        params: { slug: initialValue?.opportunity },
      });
    } else {
      cancelRegistration();
    }
  };

  const [
    newApplicationMutation,
    { loading: mutationLoading, error: mutationError, data: mutationData },
  ] = useMutation(CREATE_APPLICANT_FEEDBACK_MUTATION, {
    onCompleted: newApplicationCompleted,
  });

  const [
    updateNewApplicationMutation,
    {
      loading: updateNewMutationLoading,
      error: updateNewMutationError,
      data: updateNewMutationData,
    },
  ] = useMutation(UPDATE_APPLICANT_FEEDBACK_MUTATION, {
    onCompleted: newApplicationCompleted,
  });

  const createApplicationHandler = (values) => {
    const { opportunity, fields, members } = values;
    let applicationInput;

    let newFields = Object.keys(fields).map((key) => ({ field: key, value: fields[key] }));
    // let newFields = [...fields];
    members.forEach((member, index) => {
      const newMember = member.map((elem) => ({
        ...elem,
        group_idx: index,
      }));
      newFields = [...newFields, ...newMember];
    });

    if (feedbackDataId) {
      applicationInput = {
        fields: newFields,
      };
    } else {
      applicationInput = {
        opportunity,
        fields: newFields,
      };
    }

    if (!mutationData) {
      if (feedbackDataId) {
        updateNewApplicationMutation({
          variables: {
            id: feedbackDataId,
            input: applicationInput,
          },
        });
      } else {
        newApplicationMutation({
          variables: {
            input: applicationInput,
          },
        });
      }
    }
  };

  const getValidationSchema = (form) => {
    const fieldsValidation = {};
    const membersValidation = {};

    form.fields.forEach((field) => {
      if (field.is_required && field.type !== FIELDS_ENUM.COLLABORATORS) {
        if (field.type === FIELDS_ENUM.EMAIL) {
          fieldsValidation[field.id] = Yup.string()
            .email(t('common:Invalid email'))
            .required(t('common:validation.req'));
        } else {
          fieldsValidation[field.id] = Yup.mixed().required(t('common:validation.req'));
        }
      }

      if (field.is_required && field.type === FIELDS_ENUM.COLLABORATORS) {
        membersValidation.members = Yup.array()
          .required(t('common:validation.req'))
          .min(1, t('common:validation.one_member'));
      }
    });

    const validationList = {
      fields: Yup.object().shape({ ...fieldsValidation }),
      ...membersValidation,
    };

    const validationSchema = Yup.object().shape(validationList);
    return validationSchema;
  };

  const generateFormikValues = (viewApplicationData) => {
    const initialValues = {
      fields: {},
      members: [],
    };

    viewApplicationData &&
      viewApplicationData?.forEach((element) => {
        if (element?.value) {
          initialValues.fields[element.field_id] = element.value;
        } else if (element?.child_elements?.length > 0) {
          const groupValues = element.child_elements.map((group, groupIdx) =>
            group.map((item) => ({
              field: item.field_id,
              value: item.value,
              group_idx: groupIdx,
            }))
          );
          initialValues.members.push(...groupValues);
        }
      });
    return initialValues;
  };
  const registrationUpdatedValues = generateFormikValues(feedbackApplicationData);

  return (
    <>
      <MutationsStatus
        loading={mutationLoading || updateNewMutationLoading}
        error={mutationError || updateNewMutationError}
        success={mutationData || updateNewMutationData}
      />

      <Spacer height="2rem" width="100%" />

      <FormField item {...gridSizes}>
        <Typography className="fieldTitle" weight="600" variant="subtitle1">
          {t('applications:feedback_form')}
        </Typography>
      </FormField>

      <>
        {feedbackForm && (
          <Formik
            initialValues={{
              opportunity: initialValue.opportunity,
              fields: feedbackApplicationData
                ? registrationUpdatedValues.fields
                : initialValue.fields,
              members: feedbackApplicationData
                ? registrationUpdatedValues.members
                : initialValue.members,
            }}
            enableReinitialize
            onSubmit={createApplicationHandler}
            validationSchema={getValidationSchema(feedbackForm)}
          >
            {({
              values,
              errors,
              handleChange,
              handleBlur,
              handleSubmit,
              setValues,
              validateForm,
              setFieldValue,
              // dirty
            }) => {
              // useWarnIfUnsavedChanges(dirty);

              const updateFieldValue = (field, value) => {
                const newVals = { ...values };
                newVals.fields[field.id] = value;
                setValues(newVals);
                if (!feedbackDataId) {
                  setInitialValue(newVals);
                }
              };

              const getFieldValue = (field) => {
                if (field.type === FIELDS_ENUM.DATETIME_FIELD && !values.fields[field.id]) {
                  return null;
                }

                return values.fields[field.id];
              };

              const getFieldError = (field) => errors.fields && errors.fields[field.id];

              const textChangeHandler = (field) => (e) => {
                updateFieldValue(field, e.target.value.trim() ? e.target.value : '');
              };

              const phoneChangeHandler = (field) => (value) => {
                updateFieldValue(field, value);
              };

              const handleChangeLocation = (field) => (event, value) => {
                updateFieldValue(field, value ? value.description : null);
              };

              /**
               * @description Handler to change opportunity type array based on selected checkbox
               * @param {*} event Checkbox event
               */
              const checkboxListChangeHandler = (field) => (event) => {
                const oldValue = getFieldValue(field);
                const newValues = oldValue || [];
                if (event.target.checked) {
                  newValues.push(event.target.value);
                } else {
                  newValues.splice(newValues.indexOf(event.target.value), 1);
                }
                updateFieldValue(field, newValues);
              };
              
              const submitForm = async () => {
                try {
                  const err = await validateForm();

                  if (err && Object.keys(err).length === 0 && err.constructor === Object) {
                    handleSubmit();
                  } else {
                    // Scroll to the first error
                    const firstError = Object.keys(err)[0];

                    if (firstError !== 'fields') {
                      const selector = `[id="${firstError}"]`;
                      const errorElement = document.querySelector(selector);
                      if (errorElement) {
                        errorElement.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                          inline: 'start',
                        });
                        setTimeout(() => {
                          errorElement.focus();
                        }, 1100);
                      }
                    } else {
                      const fieldsErrors = Object.keys(err.fields);
                      const selector = `[name="${fieldsErrors[0]}"]`;
                      const errorElement = document.querySelector(selector);
                      if (errorElement) {
                        errorElement.scrollIntoView({
                          behavior: 'smooth',
                          block: 'center',
                          inline: 'start',
                        });
                        setTimeout(() => {
                          errorElement.focus();
                        }, 1100);
                      }
                    }
                  }
                } catch (error) {
                  console.error('Error during submitForm:', error);
                }
              };

              const setMembers = (members) => {
                const newValues = { ...values };
                newValues.members = members;
                setValues(newValues);
                if (!feedbackDataId) {
                  setInitialValue(newValues);
                }
              };

              const renderMandatoryFields = (fields) =>
                fields.map((field) => {
                  let fieldView = '';
                  let label;

                  try {
                    label = JSON.parse(field.label).en;
                  } catch (error) {
                    label = field.label;
                  }

                  if (field.type === FIELDS_ENUM.FULL_NAME) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_name')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.EMAIL) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            type="email"
                            label={label}
                            placeholder={t('applications:enter_email')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            value={values && getFieldValue(field)}
                            error={getFieldError(field)}
                            helperText={getFieldError(field)}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.ORGANIZATION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_org')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.LOCATION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <LocationInput
                            name={field.id}
                            label={label}
                            placeholder={t('applications:enter_location')}
                            InputLabelProps={{ shrink: true }}
                            onChange={handleChangeLocation(field)}
                            onBlur={handleBlur}
                            value={values && getFieldValue(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            error={getFieldError(field)}
                            helperText={getFieldError(field)}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  }
                  return fieldView;
                });

              const renderFields = (fields) =>
                fields.map((field) => {
                  let fieldView = '';
                  let label;

                  try {
                    label = JSON.parse(field.label).en;
                  } catch (error) {
                    label = field.label;
                  }

                  if (field.type === FIELDS_ENUM.HEADING) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box marginTop="0.5rem">
                          <Typography variant={field.subtype} detectUrls>
                            {label}
                          </Typography>
                        </Box>
                      </FormField>
                    );
                  }
                  // else if (field.type === FIELDS_ENUM.FULL_NAME) {
                  //   fieldView = (
                  //     <FormField item {...gridSizes}>
                  //       <Box className="inputBox">
                  //         <TextField
                  //           value={values && getFieldValue(field)}
                  //           label={label}
                  //           placeholder={t('applications:enter_name')}
                  //           InputLabelProps={{ shrink: true }}
                  //           name={field.id}
                  //           onChange={textChangeHandler(field)}
                  //           onBlur={handleBlur}
                  //           error={getFieldError(field)}
                  //           // error={getFieldTouched(field) && getFieldError(field)}
                  //           helperText={getFieldError(field)}
                  //         // error={touched[field.id] && errors[field.id]}
                  //         // helperText={errors[field.id]}
                  //         />
                  //       </Box>
                  //       <Spacer height="1rem" />
                  //     </FormField>
                  //   );
                  // } 
                  // else if (field.type === FIELDS_ENUM.EMAIL) {
                  //   fieldView = (
                  //     <FormField item {...gridSizes}>
                  //       <Box className="inputBox">
                  //         <TextField
                  //           type="email"
                  //           label={label}
                  //           placeholder={t('applications:enter_email')}
                  //           InputLabelProps={{ shrink: true }}
                  //           name={field.id}
                  //           onChange={textChangeHandler(field)}
                  //           onBlur={handleBlur}
                  //           value={values && getFieldValue(field)}
                  //           error={getFieldError(field)}
                  //           helperText={getFieldError(field)}
                  //         />
                  //       </Box>
                  //       <Spacer height="1rem" />
                  //     </FormField>
                  //   );
                  // } 
                  else if (field.type === FIELDS_ENUM.ORGANIZATION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_org')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.LOCATION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <LocationInput
                            name={field.id}
                            label={label}
                            placeholder={t('applications:enter_location')}
                            InputLabelProps={{ shrink: true }}
                            onChange={handleChangeLocation(field)}
                            onBlur={handleBlur}
                            value={values && getFieldValue(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            error={getFieldError(field)}
                            helperText={getFieldError(field)}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.NATIONALITY) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_nationality')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.APPLICATION_LOCATION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <LocationInput
                            name={field.id}
                            label={label}
                            placeholder={t('applications:enter_location')}
                            InputLabelProps={{ shrink: true }}
                            onChange={handleChangeLocation(field)}
                            onBlur={handleBlur}
                            value={values && getFieldValue(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            error={getFieldError(field)}
                            helperText={getFieldError(field)}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.GENDER) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box marginLeft="1rem" marginTop="1rem" width="100%">
                          <Radio.RadioGroup
                            aria-label="target"
                            name={field.id}
                            value={getFieldValue(field)}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                          >
                            <Grid container direction="row" spacing="1">
                              <Grid item xs={12}>
                                <Radio
                                  id="gender"
                                  label="Male"
                                  value="male"
                                  color="primary"
                                  inputProps={{
                                    'aria-label': 'primary Radio',
                                  }}
                                  onBlur={handleBlur}
                                />
                                <Radio
                                  id="gender"
                                  label="Female"
                                  value="female"
                                  color="primary"
                                  inputProps={{
                                    'aria-label': 'primary Radio',
                                  }}
                                  onBlur={handleBlur}
                                />
                                <Radio
                                  id="gender"
                                  label="Other"
                                  value="other"
                                  color="primary"
                                  inputProps={{
                                    'aria-label': 'primary Radio',
                                  }}
                                  onBlur={handleBlur}
                                />
                              </Grid>
                            </Grid>
                          </Radio.RadioGroup>
                          <FieldErrors error={getFieldError(field)} />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.PRIMARY_JOB) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_job')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.LONG_QUESTION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_long_question')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            multiline
                            rows={4}
                            rowsMax={15}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.SHORT_QUESTION) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_short_question')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.COLLABORATORS) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Typography className="fieldTitle" weight="600" variant="subtitle1">
                          {label} {field.is_required && '*'}
                        </Typography>
                        <>
                          <GroupMembers
                            // instrumentsList={instrumentsList}
                            name="members"
                            id="members"
                            groupField={field}
                            setMembers={setMembers}
                            members={values.members}
                          />
                          {errors.members && (
                            <FormHelperText error>{errors.members}</FormHelperText>
                          )}
                        </>
                        <Spacer height="4rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.SUBMISSION_ID) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            placeholder={t('applications:enter_submission')}
                            InputLabelProps={{ shrink: true }}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.BEST_PAPER_AWARD) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">{t('toggle')}</Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (field.type === FIELDS_ENUM.TEL) {
                    // TODO: Add phone number
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <PhoneInput
                            value={
                              feedbackDataId
                                ? `+${getFieldValue(field)}`
                                : getFieldValue(field) || ''
                            }
                            onChange={phoneChangeHandler(field)}
                            label={label}
                            placeholder={t('applications:enter_phone')}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{ id: field.id, name: field.id }}
                            containerStyle={{
                              width: '100%',
                              height: '56px',
                            }}
                            inputStyle={{
                              width: '100%',
                              height: '56px',
                              paddingLeft: '80px',
                            }}
                            buttonStyle={{
                              padding: '10px',
                            }}
                          />
                        </Box>
                        <Spacer height="1rem" />
                        <FieldErrors error={getFieldError(field)} />
                      </FormField>
                    );
                  } else if (
                    field.type === FIELDS_ENUM.TEXT_FIELD &&
                    field.subtype === SUBTYPE_ENUM.TEXT
                  ) {
                    // TODO: Handle link validation.
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            error={getFieldError(field)}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            helperText={getFieldError(field)}
                          // error={touched[field.id] && errors[field.id]}
                          // helperText={errors[field.id]}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (
                    field.type === FIELDS_ENUM.TEXT_FIELD &&
                    field.subtype === SUBTYPE_ENUM.TEXTAREA
                  ) {
                    fieldView = (
                      <FormField item {...gridSizes}>
                        <Box className="inputBox">
                          <TextField
                            value={values && getFieldValue(field)}
                            label={label}
                            name={field.id}
                            onChange={textChangeHandler(field)}
                            onBlur={handleBlur}
                            multiline
                            rows={4}
                            rowsMax={15}
                            // error={getFieldTouched(field) && getFieldError(field)}
                            error={getFieldError(field)}
                            helperText={getFieldError(field)}
                          />
                        </Box>
                        <Spacer height="1rem" />
                      </FormField>
                    );
                  } else if (
                    field.type === FIELDS_ENUM.DIET_ALLERGIES ||
                    field.type === FIELDS_ENUM.MULTIPLE_CHOICE
                  ) {
                    if (field.subtype === SUBTYPE_ENUM.CHECKBOX) {
                      fieldView = (
                        <>
                          <FormField item {...gridSizes}>
                            {/* <Typography variant="body1">{label}</Typography> */}

                            <FormControl
                              required
                              // error={getFieldTouched(field) && getFieldError(field)}
                              // helperText={ getFieldError(field)}
                              component="fieldset"
                            >
                              <FormLabel component="legend">{label}</FormLabel>
                              <Box marginLeft="1rem" width="100%">
                                <FormGroup>
                                  <Grid container spacing="1">
                                    {field.choices &&
                                      field.choices.length > 0 &&
                                      field.choices.map((choice) => (
                                        <Grid item xs={12}>
                                          <Checkbox
                                            label={choice}
                                            checked={
                                              getFieldValue(field) &&
                                              (getFieldValue(field) || []).includes(choice)
                                            }
                                            onChange={checkboxListChangeHandler(field)}
                                            name={field.id}
                                            value={choice}
                                            color="primary"
                                            onBlur={handleBlur}
                                            inputProps={{
                                              'aria-label': 'primary checkbox',
                                            }}
                                          />
                                        </Grid>
                                      ))}
                                  </Grid>
                                </FormGroup>
                              </Box>
                            </FormControl>
                            <FieldErrors error={getFieldError(field)} />
                          </FormField>
                          <Spacer height="1rem" width="100%" />
                        </>
                      );
                    } else if (field.subtype === SUBTYPE_ENUM.RADIO) {
                      fieldView = (
                        <>
                          <FormField item {...gridSizes}>
                            <FormLabel component="legend">{label}</FormLabel>
                            <Box marginLeft="1rem" width="100%">
                              <Radio.RadioGroup
                                aria-label="target"
                                name={field.id}
                                value={getFieldValue(field)}
                                onChange={textChangeHandler(field)}
                                onBlur={handleBlur}
                              >
                                <Grid container spacing="1">
                                  {field.choices &&
                                    field.choices.length > 0 &&
                                    field.choices.map((choice) => (
                                      <Grid item xs={12}>
                                        <Radio
                                          id={choice}
                                          label={choice}
                                          value={choice}
                                          color="primary"
                                          inputProps={{
                                            'aria-label': 'primary Radio',
                                          }}
                                          onBlur={handleBlur}
                                        />
                                      </Grid>
                                    ))}
                                </Grid>
                              </Radio.RadioGroup>
                              <FieldErrors error={getFieldError(field)} />
                            </Box>
                          </FormField>
                          <Spacer height="1rem" width="100%" />
                        </>
                      );
                    } else if (field.subtype === SUBTYPE_ENUM.DROPDOWN) {
                      fieldView = (
                        <>
                          <FormLabel component="legend">{label}</FormLabel>
                          <FormField item {...gridSizes}>
                            <TextField
                              select
                              // label={label}/
                              name={field.id}
                              onChange={textChangeHandler(field)}
                              onBlur={handleBlur}
                              value={values && getFieldValue(field)}
                              // error={getFieldTouched(field) && getFieldError(field)}
                              error={getFieldError(field)}
                              helperText={getFieldError(field)}
                            >
                              {field.choices &&
                                field.choices.map((choice) => (
                                  <MenuItem key={choice} value={choice}>
                                    {choice}
                                  </MenuItem>
                                ))}
                            </TextField>
                          </FormField>
                        </>
                      );
                    } else if (field.subtype === FIELDS_ENUM.HEADING) {
                      fieldView = (
                        <FormField item {...gridSizes}>
                          <Box marginTop="0.5rem">
                            <Typography variant={field.subtype} detectUrls>
                              {label}
                            </Typography>
                          </Box>
                        </FormField>
                      );
                    }
                  }

                  return fieldView;
                });

              return (
                <>
                  {/* <Grid container justify="flex-start"> */}
                  <Grid item {...gridSizes}>
                    {/* <Grid container> */}
                    <FormField item {...gridSizes}>
                      <Spacer height="2rem" />
                    </FormField>
                    {!feedbackForm.is_anonymous && renderMandatoryFields(feedbackForm.fields)}
                    {renderFields(feedbackForm.fields)}
                    {/* </Grid> */}
                  </Grid>
                  {/* </Grid> */}
                  {feedbackForm && (
                    // <Grid item {...gridSizes}>
                    <Grid container>
                      <Grid item xs={0} sm={4} />
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        marginTop="2rem"
                        marginBottom="3rem"
                        display="flex"
                        justifyContent="center"
                      >
                        <ApplyButton
                          aria-label="cancel"
                          variant="extended"
                          color="secondary"
                          loading={mutationLoading}
                          onClick={handleBack}
                        >
                          {t('common:cancel')}
                        </ApplyButton>
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        sm={4}
                        marginTop="2rem"
                        marginBottom="3rem"
                        display="flex"
                        justifyContent="center"
                      >
                        <ApplyButton
                          aria-label="apply"
                          variant="extended"
                          color="primary"
                          loading={mutationLoading}
                          onClick={submitForm}
                        >
                          {feedbackApplicationData ? t('applications:update') : t('common:submit')}
                          <ChevronRightIcon />
                        </ApplyButton>
                      </Grid>
                    </Grid>
                    // </Grid>
                  )}
                </>
              );
            }}
          </Formik>
        )}
      </>
    </>
  );
};

FeedbackForm.propTypes = {
  feedbackApplicationData: PropTypes.object,
  feedbackForm: PropTypes.object.isRequired,
  instruments: PropTypes.array.isRequired,
  genres: PropTypes.array.isRequired,
  initialValue: PropTypes.object,
  setInitialValue: PropTypes.func,
  feedbackDataId: PropTypes.string,
  cancelRegistration: PropTypes.func,
  giveFeedback: PropTypes.bool,
  refetch: PropTypes.func,
};

FeedbackForm.defaultProps = {
  feedbackDataId: null,
  initialValue: {
    opportunity: '',
    fields: {},
    members: [],
  },
  setInitialValue: () => { },
  cancelRegistration: () => { },
  feedbackApplicationData: null,
  giveFeedback: false,
  refetch: () => { },
};

export default FeedbackForm;
